import {
  ColumnActionsMode,
  IColumn,
  IContextualMenuItem,
  IContextualMenuProps,
  IconButton
} from "@fluentui/react";
import { createElement } from "react";

import {
  contractShipmentStatusColors,
  contractShipmentStatusLabels,
  contractShipmentTypeLabels,
  ContractShipmentStatus,
  type ContractShipmentAction,
  type ListContractShipmentsResponseItem
} from "@/interfaces/modules/contract-shipments";
import { formatCentsToCurrency } from "@/utils/FormatBRL";
import { formatISOStringToDateString } from "@/utils/FormatISOStringToDateString";

interface ContractShipmentColumnsParams {
  canUpdate: boolean;
  canListCosts: boolean;
  isLoading: boolean;
  openDialog: (
    action: ContractShipmentAction,
    shipment?: ListContractShipmentsResponseItem
  ) => void;
}

const defaultContractShipmentColumns: Array<IColumn> = [
  {
    key: "type",
    name: "Tipo",
    fieldName: "type",
    minWidth: 90,
    maxWidth: 110,
    onRender: (shipment: ListContractShipmentsResponseItem) =>
      contractShipmentTypeLabels[shipment.type]
  },
  {
    key: "code",
    name: "Código",
    fieldName: "code",
    minWidth: 130,
    isResizable: true
  },
  {
    key: "cost",
    name: "Custo do envio",
    fieldName: "cost",
    minWidth: 110,
    onRender: (shipment: ListContractShipmentsResponseItem) =>
      "cost" in shipment ? formatCentsToCurrency(shipment.cost) : "—"
  },
  {
    key: "receivedAt",
    name: "Recebimento",
    fieldName: "receivedAt",
    minWidth: 100,
    onRender: (shipment: ListContractShipmentsResponseItem) =>
      shipment.receivedAt
        ? formatISOStringToDateString(shipment.receivedAt.slice(0, 10))
        : "—"
  },
  {
    key: "notes",
    name: "Anotações",
    fieldName: "notes",
    minWidth: 280,
    maxWidth: 500,
    isResizable: true,
    isMultiline: true,
    onRender: (shipment: ListContractShipmentsResponseItem) =>
      createElement(
        "span",
        {
          style: {
            display: "-webkit-box",
            overflow: "hidden",
            overflowWrap: "anywhere",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 5
          }
        },
        shipment.notes || "—"
      )
  },
  {
    key: "status",
    name: "Status",
    fieldName: "status",
    minWidth: 130,
    onRender: (shipment: ListContractShipmentsResponseItem) =>
      createElement(
        "span",
        {
          style: {
            color: contractShipmentStatusColors[shipment.status],
            fontWeight: 600
          }
        },
        contractShipmentStatusLabels[shipment.status]
      )
  }
];

export const contractShipmentColumns = ({
  canUpdate,
  canListCosts,
  isLoading,
  openDialog
}: ContractShipmentColumnsParams): Array<IColumn> => {
  const columns = defaultContractShipmentColumns.filter(column => {
    if (column.key === "cost") {
      return canListCosts;
    }

    return true;
  });

  if (!canUpdate) return columns;

  const actionColumn: IColumn = {
    key: "actions",
    name: "Ações",
    fieldName: "actions",
    minWidth: 40,
    maxWidth: 40,
    columnActionsMode: ColumnActionsMode.disabled,
    isIconOnly: true,
    onRender: (shipment: ListContractShipmentsResponseItem) => {
      if (shipment.status !== ContractShipmentStatus.IN_TRANSIT) return null;

      const menuItems: Array<IContextualMenuItem> = [
        {
          key: "edit",
          text: "Editar",
          onClick: () => openDialog("EDIT", shipment)
        },
        {
          key: "confirmDelivery",
          text: "Confirmar Entrega",
          onClick: () => openDialog("CONFIRM_DELIVERY", shipment)
        },
        {
          key: "cancel",
          text: "Cancelar Envio",
          onClick: () => openDialog("CANCEL", shipment)
        }
      ];

      const menuProps: IContextualMenuProps = {
        shouldFocusOnMount: true,
        items: menuItems
      };

      return createElement(IconButton, {
        id: `shipment-actions-${shipment.id}`,
        title: "Ações do envio",
        ariaLabel: `Ações do envio ${shipment.code}`,
        menuProps,
        iconProps: { iconName: "MoreVertical" },
        onRenderMenuIcon: () => null,
        disabled: isLoading
      });
    }
  };

  return [...columns, actionColumn];
};
